import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { fetchOnboardingStatus } from './api';
import { isEligibleForRetake } from './retakeEligibility';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { isOnboardingRoute } from '@/routes/onboarding/utils/isOnboardingRoute';
import { useOnboardingStateContext } from './OnboardingStateContext';

/** Routes that should bypass the onboarding guard entirely (public quiz flow) */
const isPublicQuizRoute = (path: string) =>
  path === '/quiz' || path.startsWith('/quiz/');

/** Only these protected routes enforce onboarding completion */
const requiresOnboarding = (path: string) =>
  path === '/dashboard' ||
  path === '/future-me' ||
  path === '/transactions' ||
  path === '/goals' ||
  path === '/wishlist' ||
  path.startsWith('/settings') ||
  (path.startsWith('/billing') && path !== '/billing/success') ||
  path.startsWith('/admin');

/** Auth routes that must bypass the onboarding guard so callbacks can process */
const isAuthRoute = (path: string) =>
  path === '/auth/callback' || path === '/auth/confirm' ||
  path === '/auth/signin' || path === '/auth/signup' ||
  path === '/auth/reset';

type Props = {
  children: React.ReactNode;
};

// Pure helper (no hooks)
const withNext = (path: string, next: string) => (next ? `${path}?next=${encodeURIComponent(next)}` : path);

// Robust retake parsing: accept 1/true/yes/y, case-insensitive, or bare "?retake"
const parseRetake = (search: string): boolean => {
  const sp = new URLSearchParams(search);
  if (!sp.has('retake')) return false;
  const raw = (sp.get('retake') ?? '').trim().toLowerCase();
  return raw === '' || raw === '1' || raw === 'true' || raw === 'yes' || raw === 'y';
};

export default function RequireOnboarding({ children }: Props) {
  // ---- HOOKS (always called, stable order) ----
  const location = useLocation();
  const { toast } = useToast();
  const { state: onboardingState } = useOnboardingStateContext();

  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [user, setUser] = useState<any>(null);
  const [redirectToProfile, setRedirectToProfile] = useState(false);

  // Auth check — initial read + reactive subscription so user.id stays in sync
  // after sign-in/sign-out swaps. Without onAuthStateChange, a stale user.id
  // from a previous session can drive the onboarding query and redirect a
  // returning user with a completed profile to /quiz.
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setIsAuthenticated(Boolean(data?.session));
      setUser(data?.session?.user || null);
    });

    // Subscribe to auth changes when available (real Supabase client).
    // Tests using a minimal auth mock may not provide onAuthStateChange — in
    // that case we silently skip the subscription; getSession is sufficient there.
    let unsubscribe: (() => void) | null = null;
    if (typeof (supabase.auth as any).onAuthStateChange === 'function') {
      const { data: sub } = (supabase.auth as any).onAuthStateChange(
        (_event: string, session: any) => {
          if (!mounted) return;
          setIsAuthenticated(Boolean(session));
          setUser(session?.user || null);
        }
      );
      unsubscribe = () => sub?.subscription?.unsubscribe?.();
    }

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Derived values (no hooks below)
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const existingNext = searchParams.get('next');
  // Only compute next from current location if NOT on an onboarding route
  const next = existingNext || (!isOnboardingRoute(location.pathname) 
    ? location.pathname + location.search + location.hash 
    : '');
  // Retake mode is active if URL has ?retake=1 OR global retakeMode === true
  const isRetakeMode = useMemo(
    () => parseRetake(location.search) || onboardingState.retakeMode === true,
    [location.search, onboardingState.retakeMode]
  );

  // Only fetch onboarding status when on a route that actually needs it
  const shouldCheckOnboardingStatus =
    isAuthenticated === true &&
    !isAuthRoute(location.pathname) &&
    !isPublicQuizRoute(location.pathname) &&
    (requiresOnboarding(location.pathname) || isOnboardingRoute(location.pathname));

  // Onboarding status (hook always mounted; network gated)
  const { data: onboarded, isLoading } = useQuery({
    queryKey: ['onboardingStatus', user?.id],
    queryFn: () => fetchOnboardingStatus(user!.id),
    enabled: shouldCheckOnboardingStatus && !!user?.id,
    staleTime: 5_000,
    retry: 2,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Latest quiz attempt date (hook always mounted; network gated)
  const { data: quizAttempt } = useQuery<Date | null>({
    queryKey: ['latestQuizAttempt', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('quiz_results')
        .select('created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data ? new Date(data.created_at) : null;
    },
    enabled: isAuthenticated === true && onboarded === true && isRetakeMode,
  });

  // Retake eligibility side-effects (toast + redirect flag), separated from render
  useEffect(() => {
    if (onboarded === true && isRetakeMode && quizAttempt !== undefined) {
      const eligibility = isEligibleForRetake(quizAttempt ?? null);
      if (!eligibility.eligible && eligibility.nextEligible) {
        const friendlyDate = format(eligibility.nextEligible, 'PPP');
        toast({
          title: 'Quiz Retake Not Available',
          description: `You can retake on ${friendlyDate} (in ${eligibility.remainingDays} days).`,
          variant: 'destructive',
        });
        setRedirectToProfile(true);
      }
    }
  }, [onboarded, isRetakeMode, quizAttempt, toast]);

  // ---- RENDER DECISIONS (no hooks below) ----

  // Auth status loading → minimal placeholder to avoid flicker
  if (isAuthenticated === null) return null;

  // Public quiz routes bypass guard entirely
  if (isPublicQuizRoute(location.pathname)) return <>{children}</>;

  // Auth routes bypass guard so callbacks can process sessions
  if (isAuthRoute(location.pathname)) return <>{children}</>;

  // Not authenticated → let upstream auth layer handle it
  if (!isAuthenticated) return <>{children}</>;

  // Onboarding status loading → visible spinner instead of blank screen
  if (shouldCheckOnboardingStatus && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // If retake is disallowed, navigate to profile (after toast side-effect)
  if (redirectToProfile) {
    return <Navigate to="/settings/profile" replace />;
  }

  // Not onboarded and on a protected route → force Step 1
  // Skip redirect if quiz was just completed this session (cache may be stale)
  if (onboarded === false && requiresOnboarding(location.pathname) && !onboardingState.quizJustCompleted) {
    return <Navigate to="/quiz" replace />;
  }

  // Onboarded and inside onboarding flow (but not retaking) → send to dashboard
  if (onboarded === true && isOnboardingRoute(location.pathname) && !isRetakeMode) {
    return <Navigate to="/dashboard" replace />;
  }

  // Otherwise render the requested page
  return <>{children}</>;
}
